import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import { connect } from "react-redux";

import Input, { InputRef } from "antd/es/input";
import { defineMessages, useIntl } from "react-intl";

import MapTool from "@mapmycustomers/shared/enum/map/MapTool";
import Address from "@mapmycustomers/shared/types/Address";
import { GeocodeResult } from "@mapmycustomers/shared/types/base/Located";
import {
  convertLongLatToLatLngLiteral,
  createCoordinates,
} from "@mapmycustomers/shared/util/geo/GeoService";
import { AutoCompleteAddress, Button } from "@mapmycustomers/ui";

import poweredByGoogle from "@app/assets/google/poweredByGoogle.png";
import ExtendedAnalytics from "@app/component/analytics/ExtendedAnalytics";
import { useMap } from "@app/component/map/FeaturedMap";
import {
  selectMapTool,
  setLocationSearchVisibility,
  updatePinDropCoordinates,
} from "@app/scene/map/store/actions";
import { geocodeAddress, reverseGeocodeAddress } from "@app/store/location/actions";
import useAnalytics from "@app/util/contexts/useAnalytics";

import styles from "./GoToLocationPopover.module.scss";
import getAddressForStateAndCountry from "./util/createAddressForStateAndCountry/getAddressForStateAndCountry";
import getZoomLevelByAddress from "./util/createAddressForStateAndCountry/getZoomLevelByAddress";

const TYPES: string[] = ["geocode"];

interface Props {
  onGeocodeAddress: typeof geocodeAddress;
  onReverseGeocodeAddress: typeof reverseGeocodeAddress;
  onSelectMapTool: typeof selectMapTool;
  onSetLocationSearchVisibility: typeof setLocationSearchVisibility;
  onUpdatePinDropCoordinates: typeof updatePinDropCoordinates.request;
  setLookUpLocation?: (lookUpLocation: google.maps.LatLngLiteral) => void;
}

const messages = defineMessages({
  description: {
    id: "map.tool.goToLocation.popover.description",
    defaultMessage: "Search for an address to go there on the map",
    description: "Go to location popover query change warning",
  },
  placeholderMessage: {
    id: "map.tool.goToLocation.popover.placeholder",
    defaultMessage: "Search for address",
    description: "Go to location popover placeholder",
  },
  title: {
    id: "map.tool.goToLocation.popover.title",
    defaultMessage: "Go to location",
    description: "Go to location popover title",
  },
});

const GoToLocationPopover: React.FC<Props> = ({
  onGeocodeAddress,
  onReverseGeocodeAddress,
  onSelectMapTool,
  onSetLocationSearchVisibility,
  onUpdatePinDropCoordinates,
  setLookUpLocation,
}) => {
  const analytics = useAnalytics();
  const intl = useIntl();
  const { map } = useMap();

  const [address, setAddress] = useState<Address | undefined>();
  const inputRef = useRef<InputRef>(null);

  const handleClick = useCallback(() => {
    analytics.completed(["Click go to location button"]);
    if (address) {
      const updatedAddress =
        !address.city && !address.address ? getAddressForStateAndCountry({ ...address }) : address;
      onGeocodeAddress({
        address: updatedAddress,
        callback: (result: GeocodeResult) => {
          const coordinates = result.geoPoint?.coordinates;
          if (coordinates && address) {
            setLookUpLocation?.(convertLongLatToLatLngLiteral(coordinates));
            map.panTo(convertLongLatToLatLngLiteral(coordinates));
            map.setZoom(getZoomLevelByAddress(address));
            onSetLocationSearchVisibility(false);

            const geoCoordinates = createCoordinates({
              latitude: coordinates[1],
              longitude: coordinates[0],
            });
            onUpdatePinDropCoordinates(geoCoordinates);
            onSelectMapTool({ mapTool: MapTool.DROP_PIN });
          }
        },
      });
    }
  }, [
    address,
    analytics,
    map,
    onGeocodeAddress,
    onSelectMapTool,
    onSetLocationSearchVisibility,
    onUpdatePinDropCoordinates,
    setLookUpLocation,
  ]);

  useLayoutEffect(() => {
    inputRef.current?.focus();
  }, [inputRef]);

  const onPressEnter = useCallback(() => {
    if (address) {
      inputRef.current?.blur();
      handleClick();
    }
  }, [address, handleClick, inputRef]);

  return (
    <ExtendedAnalytics keyPath="Lead Finder Popover">
      <div className={styles.container}>
        <div className={styles.title}>
          {intl.formatMessage(messages.title)}
          <img alt="powered by Google" className={styles.poweredByGoogle} src={poweredByGoogle} />
        </div>
        <div className={styles.description}>{intl.formatMessage(messages.description)}</div>
        <Input.Group className={styles.searchRow} compact>
          <AutoCompleteAddress
            className={styles.searchBar}
            colProps={{ className: styles.searchBarCol }}
            disableFindMe
            onChange={setAddress}
            onPressEnter={onPressEnter}
            onReverseGeocodeAddress={onReverseGeocodeAddress}
            placeholder={intl.formatMessage(messages.placeholderMessage)}
            placePredictionTypes={TYPES}
            ref={inputRef}
            value={address}
          />
          <Button onClick={handleClick} size="large" type="primary">
            {intl.formatMessage({
              id: "map.tool.goToLocation.popover.go",
              defaultMessage: "Go",
              description: "Map Go to location tool Go button",
            })}
          </Button>
        </Input.Group>
      </div>
    </ExtendedAnalytics>
  );
};

const mapDispatchToProps = {
  onGeocodeAddress: geocodeAddress,
  onReverseGeocodeAddress: reverseGeocodeAddress,
  onSelectMapTool: selectMapTool,
  onSetLocationSearchVisibility: setLocationSearchVisibility,
  onUpdatePinDropCoordinates: updatePinDropCoordinates.request,
};

export default connect(null, mapDispatchToProps)(GoToLocationPopover);
